import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import useSetState from '#utils/use-set-state';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';

import {
  useGetReceivingMutation,
  useSaveReceivingMutation,
} from '#services/receiving';
import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import BarcodeInputBox from '#components/BarcodeInputBox';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import URLSearchParamsEx from '#utils/url-ex';
import useUnsavedChangesWarning from '#hooks/useUnsavedChangesWarning';

interface ReceivingItem {
  barcode?: string;
  orderId?: string | number;
  bulkOrderId?: string | number;
  inventoryOrderId?: string | number;
  vendor?: string;
  hasRelatedOrders?: boolean;
  [key: string]: unknown;
}

interface ReceivingData {
  id: string | number | null;
  items: ReceivingItem[];
  createdBy: string;
  createdDate: Date | string;
  receivingIndex?: number | undefined;
  total?: number | undefined;
  [key: string]: unknown;
}

interface Diff {
  event: string;
  scope: string;
  data: unknown;
}

export default function ReceivingEdit() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [getReceiving] = useGetReceivingMutation();
  const [saveReceiving] = useSaveReceivingMutation();

  const currentUsername = useSelector((state: { auth: { userInfo: { username?: string } } }) => state.auth.userInfo.username);
  const currentUserId = useSelector((state: { auth: { userInfo: { id?: string | number } } }) => state.auth.userInfo.id);

  const [receivingData, setReceivingData] = useSetState<ReceivingData>({
    id: null,
    items: [],
    createdBy: '',
    createdDate: new Date(),
  });

  const [diffs, setDiffs] = useState<Diff[]>([]);

  useUnsavedChangesWarning({
    hasUnsavedChanges: () => diffs.length > 0,
  });

  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  useEffect(() => {
    showBackdrop();
    void getReceiving(urlParams.values)
      .then((res) => {
        hideBackdrop();
        if (res.error) {
          return;
        }

        setReceivingData(res.data as Partial<ReceivingData>);
      });
  }, [location.key]);

  const getOrderLink = (id: string | number | undefined, basePath: string) => {
    if (!id) {
      return '-';
    }

    const url = `${basePath}${id}`;
    return (
      <Link
        href={url}
        onClick={(e) => {
          e.preventDefault();
          void navigate(url);
        }}
      >
        {id}
      </Link>
    );
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'barcode',
    label: 'Barcode',
  }, {
    id: 'orderId',
    label: 'Customer Order',
    renderer: (r) => getOrderLink((r as unknown as ReceivingItem).orderId, '/management/order/detail?orderId='),
  }, {
    id: 'bulkOrderId',
    label: 'Bulk Order',
    renderer: (r) => getOrderLink((r as unknown as ReceivingItem).bulkOrderId, '/management/bulk/detail?bulkOrderId='),
  }, {
    id: 'inventoryOrderId',
    label: 'Inventory Order',
    renderer: (r) => getOrderLink((r as unknown as ReceivingItem).inventoryOrderId, '/management/inventory/detail?inventoryOrderId='),
  }, {
    id: 'vendor',
    label: 'Vendor',
    renderer: (r) => (r as unknown as ReceivingItem).vendor || '-',
  }, {
    id: 'action',
    label: 'Action',
    renderer: (row) => {
      const r = row as unknown as ReceivingItem;
      if (r.hasRelatedOrders) {
        return null;
      }

      return (
        <Link
          key="btn-delete"
          component="button"
          onClick={() => {
            const newDiffs = diffs.filter((diff) => diff.data !== r.barcode);
            if (newDiffs.length === diffs.length) {
              // item is in the list
              setDiffs([
                ...diffs,
                {
                  event: 'delete',
                  scope: 'items',
                  data: r.barcode,
                },
              ]);
            } else {
              // item is in the diffs(add just now but not saved)
              setDiffs(newDiffs);
            }
            setReceivingData({
              items: receivingData.items.filter((itm) => itm.barcode !== r.barcode),
            });
          }}
        >
          Delete
        </Link>
      );
    },
  }];

  const tableProps: Omit<EnhancedTableProps, 'name'> = {
    headers: tableHeaders,

    rows: receivingData.items,
  };

  const handleSaveReceiving = (_event: React.MouseEvent) => {
    showBackdrop();
    void saveReceiving({
      receivingId: receivingData.id,
      diffs,
    }).then((res) => {
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Receiving list has been saved.');
      if (hasError) {
        return;
      }

      const newReceivingData = {
        receivingIndex: receivingData.receivingIndex,
        total: receivingData.total,
        ...(res.data as Partial<ReceivingData>),
      };
      setReceivingData(newReceivingData);
      setDiffs([]);
    }).finally(() => {
      hideBackdrop();
    });
  };

  const createdBy = receivingData.createdBy
    || `${currentUsername}#${currentUserId!.toString().padStart(4, '0')}`;

  const handleTravelOrder = (direction: number) => {
    urlParams.set('receivingIndex', (val) => `${(val as number) + direction}`);
    const search = urlParams.toString();
    void navigate({
      pathname: '/management/receiving/edit',
      search,
    });
  };

  return (
    <>
      <TitleBar
        title="Receiving List Detail"
        hideActionButtons={!urlParams.values.receivingIndex}
      >
        <TitleBarItemTraveler
          onTravelItem={(direction) => handleTravelOrder(direction)}
          itemIndex={receivingData.receivingIndex as number}
          itemSize={receivingData.total as number}
        />
      </TitleBar>

      <Box>
        <BarcodeInputBox
          barcodes={receivingData.items.map((rd) => rd.barcode) as Array<string | number | null>}
          onSubmit={(event, newBarcode) => {
            (event as { target: HTMLInputElement }).target.value = '';
            setReceivingData({
              items: [...receivingData.items, newBarcode],
            });
            setDiffs([
              ...diffs,
              {
                event: 'add',
                scope: 'items',
                data: newBarcode.barcode,
              },
            ]);
          }}
        />
      </Box>

      <EnhancedTable
        name="variants"
        {...tableProps}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          pt: 2,
        }}
      >
        <b>Total Items</b>
        :
        {' '}
        <span data-testid="total-items">{tableProps.rows.length}</span>
      </Box>

      <Box>
        <Typography variant={'h7' as TypographyProps['variant']} gutterBottom>
          Created By
        </Typography>
      </Box>
      <Box>
        <TextField
          disabled
          size="small"
          fullWidth
          value={createdBy}
        />
      </Box>

      <Box>
        <Button
          disabled={!diffs.length}
          variant="contained"
          onClick={handleSaveReceiving}
        >
          Save
        </Button>
      </Box>

    </>
  );
}
